import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { useI18n } from '../../i18n/useI18n';
import { getAdminUserMachines, getUserMachines, MachineInfo } from '../../services/api';
import { Theme, themeService, ThemeServiceContext } from '../../services/themeService';
import { ThemeChannel } from '../../utils/themePaths';

const pinkFill = 'linear-gradient(135deg, #ffc1d4 0%, #ff8ba7 52%, #f48fb1 100%)';
const pinkHover = 'linear-gradient(135deg, #ff8ba7 0%, #f48fb1 52%, #bf5f82 100%)';

interface ThemeMachineFilterDialogProps {
  open: boolean;
  theme: Theme | null;
  channel?: ThemeChannel;
  apiCtx: ThemeServiceContext;
  userId?: string;
  onClose: () => void;
  onSaved: (theme: Theme) => void;
}

const ThemeMachineFilterDialog: React.FC<ThemeMachineFilterDialogProps> = ({
  open,
  theme,
  channel = 'pro',
  apiCtx,
  userId,
  onClose,
  onSaved,
}) => {
  const { pages: t } = useI18n();
  const isLite = channel === 'lite';
  const hasFrames = (theme?.customFrames || []).length > 0;
  const [machines, setMachines] = useState<MachineInfo[]>([]);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [codes, setCodes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !theme || !hasFrames) return;
    setCodes((theme.allowedMachineCodes || []).map((code) => code.toUpperCase()));
  }, [open, theme, hasFrames]);

  useEffect(() => {
    if (!open || !hasFrames) return;
    let cancelled = false;
    setLoadingMachines(true);
    const load = userId ? getAdminUserMachines(userId) : getUserMachines();
    load
      .then((result) => {
        if (cancelled) return;
        const matched = (result.machines || []).filter((machine) => {
          const product = String(machine.appProduct || '').toLowerCase();
          const platform = String(machine.appPlatform || '').toLowerCase();
          if (isLite) {
            if (product === 'pro' || product === 'mono') return false;
            return product === 'lite' || platform === 'android';
          }
          return product === 'pro';
        });
        setMachines(matched);
        const matchedCodes = new Set(matched.map((machine) => machine.machineCode.toUpperCase()));
        setCodes((prev) => prev.filter((code) => matchedCodes.has(code)));
      })
      .catch(() => {
        if (!cancelled) setMachines([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMachines(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, userId, hasFrames, isLite]);

  const toggleMachine = (machineCode: string) => {
    const code = machineCode.toUpperCase();
    setCodes((prev) => (prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code]));
  };

  const handleSave = async () => {
    if (!theme) return;
    setSaving(true);
    try {
      const response = await themeService.setThemeAvailability(
        theme._id,
        {
          scheduleEnabled: theme.scheduleEnabled === true,
          availableFrom: theme.availableFrom || null,
          availableUntil: theme.availableUntil || null,
          machineFilterEnabled: true,
          allowedMachineCodes: codes,
        },
        apiCtx,
      );
      const saved = (response.theme || {}) as Partial<Theme>;
      onSaved({
        ...theme,
        scheduleEnabled: saved.scheduleEnabled === true,
        availableFrom: saved.availableFrom ?? theme.availableFrom ?? null,
        availableUntil: saved.availableUntil ?? theme.availableUntil ?? null,
        machineFilterEnabled: true,
        allowedMachineCodes: saved.allowedMachineCodes || codes,
      });
      toast.success(t.themes.machineFilterSaved);
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || t.themes.machineFilterSaveFail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: '#bf5f82' }}>
        {isLite ? t.themes.machineFilterEnableLite : t.themes.machineFilterEnable}
      </DialogTitle>
      <DialogContent>
        {!hasFrames ? (
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {t.themes.machineFilterNeedFrame}
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
              <Button
                size="small"
                disabled={loadingMachines || machines.length === 0}
                onClick={() => setCodes(machines.map((machine) => machine.machineCode.toUpperCase()))}
                sx={{ color: '#bf5f82', fontWeight: 700, textTransform: 'none' }}
              >
                {t.themes.machineFilterSelectAll}
              </Button>
              <Button
                size="small"
                disabled={loadingMachines || machines.length === 0}
                onClick={() => setCodes([])}
                sx={{ color: '#bf5f82', fontWeight: 700, textTransform: 'none' }}
              >
                {t.themes.machineFilterClearAll}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
              {isLite ? t.themes.machineFilterHintLite : t.themes.machineFilterHint}
            </Typography>
            {loadingMachines ? (
              <Box display="flex" alignItems="center" gap={1} py={1}>
                <CircularProgress size={18} />
                <Typography variant="body2">{t.themes.machineFilterLoading}</Typography>
              </Box>
            ) : machines.length === 0 ? (
              <Alert severity="info">{isLite ? t.themes.machineFilterEmptyLite : t.themes.machineFilterEmpty}</Alert>
            ) : (
              <List
                dense
                sx={{
                  border: '1px solid',
                  borderColor: alpha('#ff8ba7', 0.45),
                  borderRadius: 2,
                  bgcolor: '#fff',
                  maxHeight: 320,
                  overflow: 'auto',
                }}
              >
                {machines.map((machine) => {
                  const code = machine.machineCode.toUpperCase();
                  const checked = codes.includes(code);
                  const label = machine.machineName?.trim() ? machine.machineName : code;
                  return (
                    <ListItemButton key={code} onClick={() => toggleMachine(code)} sx={{ py: 0.25 }}>
                      <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                      <Box minWidth={0}>
                        <Typography fontWeight={700} noWrap>
                          {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {code}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#bf5f82' }}>
          {hasFrames ? t.common.cancel : t.common.close}
        </Button>
        {hasFrames && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || loadingMachines}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 999,
              color: '#fff',
              background: pinkFill,
              boxShadow: 'none',
              '&:hover': { background: pinkHover },
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : t.common.save}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ThemeMachineFilterDialog;
